/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import {
  IonPage,
  IonFabButton,
  IonButton,
  IonToast,
  IonRefresher,
  IonRefresherContent,
  IonContent,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonIcon,
} from "@ionic/react";
import ReflectionHeader from "../../components/journal/ReflectionHeader";
import ReflectionsModalCard from "../../components/journal/ReflectionsModalCard";
import AddPostModal from "./AddPostModal";
import "./AddPostModal.css";
import AllFollowingList from "../../components/ui/popups/AllFollowingList";
import {
  Following,
  FollowingList,
} from "../settings/datasharing/DataSharingTypes";
import PostCard from "../../components/journal/PostCard";
import ReplyModal from "../../components/journal/ReplyModal";
import { PostData, ReactionType } from "../../types/socialJournal";
import {
  addComment,
  addCommentReaction,
  changePostPrivacy,
  deletePost,
  fetchCommentReplies,
  fetchFeed,
  Post,
  toggleCommentReaction,
} from "../../services/postsService";
import { REACTION_TO_BACKEND_MAP } from "../../utils/postDataTransform";
import Loader from "../../components/helpers/Loader";
import { useAuth } from "../../hooks/useAuth";
import CardSkeleton from "../../components/skeleton/CardSkeleton";
import ActionButtons from "../../components/ui/popups/ActionButtons";
import "./MyReflections.css";
import { mapFollowers } from "../../utils/helpers";
import { useStorage } from "../../contexts/StorageContext";
import { getIonicStorage, ionicStorage } from "../../services/storageService";
import { EmptyState } from "./subcomponents/EmptyStateReflection";
import { getAllFollowing, unfollowUser } from "../../services/datashare";
import { useToaster } from "../../components/ui/toastbar/Toaster";
import { useSocket } from "../../contexts/SocketContext";
import { add } from "ionicons/icons";
import DataRefreshPill from "../../components/ui/DataRefreshPill";
import { Preferences } from "@capacitor/preferences";
import CommentReplies from "../../components/journal/Comments/CommentReplies";

interface ActionButton {
  text: string;
  action: () => void;
  isPrimary?: boolean;
}


const MyReflections: React.FC = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState<Post[]>([]);
  const [allPosts, setAllPosts] = useState<Post[]>([]);
  const [isAddPostModalOpen, setIsAddPostModalOpen] = useState(false);
  const toaster = useToaster();
  const [isFollowingModalOpen, setOpenFollowingModal] = useState(false);
  const [followingData, setFollowingData] = useState<any>(null);
  const [preSelected, setPreSelected] = useState<
    "text" | "template" | "images" | "feelings" | null
  >(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isCreatingPost, setIsCreatingPost] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const { storage, isStorageReady } = useStorage();

  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePosts, setHasMorePosts] = useState(true);
  const [selectedProfileId, setSelectedProfileId] = useState<number | null>(
    null
  );
  const [selectedProfile, setSelectedProfile] = useState<{
    label: string;
    value: number | string;
  } | null>(null);
  const [pagination, setPagination] = useState<{
    limit: number;
    page: number;
    total: number;
    hasMore?: boolean;
  }>({
    limit: 5,
    page: 1,
    total: 15,
    hasMore: false,
  });
  const [filterType, setFilterType] = useState<
    "both" | "Me" | "followers" | "" | "one_follower"
  >("both");

  const [isReplyOpen, setIsReplyOpen] = useState(false);
  const [activePost, setActivePost] = useState<Post | null>(null);
  const [replyPage, setReplyPage] = useState(1);
  const [replyList, setReplyList] = useState<any[]>([]);
  const [totalComments, setTotalComments] = useState(0);

  const [popupContent, setPopupContent] = useState<{
    header: string;
    message: string;
    buttons: ActionButton[];
    children?: React.ReactNode;
  }>({
    header: "",
    message: "",
    buttons: [],
  });
  const [popupOpen, setPopupOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<Post | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [showTapRefresh, setShowTapRefresh] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const { reflectionsRefresh } = useSocket();
  const following: Following[] = useMemo(() => {
    return mapFollowers(followingData || []);
  }, [followingData]);

  // Indexed so each card is a lookup, not a full filter per render.
  const followingById = useMemo(() => {
    const byId = new Map<number, Following>();
    following.forEach((follower) => byId.set(follower.id, follower));
    return byId;
  }, [following]);

  const closeFollowingModal = useCallback(() => {
    setOpenFollowingModal(false);
  }, []);

  const [avatars, setAvatars] = useState<{ label: string; value: number }[]>(
    []
  );

  // ============ STORAGE UTILITY FUNCTIONS ============
  /**
   * Save the current filter and profile preferences to Capacitor Preferences
   */
  const saveFilterPreferences = useCallback(
    async (
      newFilterType: typeof filterType,
      newSelectedProfileId: number | null,
      newSelectedProfile: typeof selectedProfile
    ) => {
      try {
        // Save filter type
        await Preferences.set({
          key: "LAST_FILTER",
          value: newFilterType || "both",
        });
      } catch (error) {
        console.error("Error saving filter preferences:", error);
      }
    },
    []
  );

  /**
   * Load the saved filter and profile preferences from Capacitor Preferences
   */
  const loadFilterPreferences = useCallback(async () => {
    try {
      const { value: savedFilter } = await Preferences.get({
        key: "LAST_FILTER",
      });

      if (savedFilter) {
        setFilterType(savedFilter as typeof filterType);
      }
    } catch (error) {
      console.error("Error loading filter preferences:", error);
    }
  }, []);

  // ============ INITIALIZATION ============

  /**
   * Load following data from storage or API
   */
  const loadFollowingFromStorage = async () => {
    if (!storage || !isStorageReady) return;

    const storedFollowing = await getIonicStorage(storage, "following");
    if (storedFollowing?.length) {
      setFollowingData(storedFollowing);
    }
    fetchFollowingData();
  };

  /**
   * Store following data to ionic storage
   */
  const storeFollowingToStorage = useCallback(
    async (followingList: any) => {
      if (!storage || !isStorageReady) return;

      try {
        await ionicStorage(storage, "following", JSON.stringify(followingList));
      } catch (error) {
        console.error("Error storing following data:", error);
      }
    },
    [storage, isStorageReady]
  );

  /**
   * Fetch following data from API
   */
  const fetchFollowingData = async () => {
    try {
      const allFollowingStorage: FollowingList[] = await getIonicStorage(
        storage,
        "following"
      );
      // ⚠️ The `FollowingList[]` annotation is a lie: `getIonicStorage` is `Promise<any>` and
      // yields null for a key never written, so on a cold start this was `null.map(...)` — which
      // threw INSIDE the try/catch and so silently skipped the `getAllFollowing()` refresh
      // below, leaving the filter permanently empty rather than crashing visibly.
      const avatarStorageOptions = (allFollowingStorage || []).map((f) => ({
        label: f?.profile?.avatar || f?.profile?.first_name?.charAt(0) || "",
        value: f?.profile?.id,
      }));

      setAvatars(avatarStorageOptions);
      setFollowingData(allFollowingStorage);
      const allFollowing: FollowingList[] = await getAllFollowing();
      const avatarOptions = (allFollowing || []).map((f) => ({
        label: f?.profile?.avatar || f?.profile?.first_name?.charAt(0) || "",
        value: f?.profile?.id,
      }));

      setAvatars(avatarOptions);
      setFollowingData(allFollowing);

      // Store to ionic storage for offline access
      await storeFollowingToStorage(allFollowing);
    } catch (error) {
      console.error("Error fetching following data:", error);
    }
  };

  /**
   * Initialize component on first load - load filter preferences and posts
   */
  useEffect(() => {
    const initializeComponent = async () => {
      if (!isStorageReady || isInitialized) return;

      loadFollowingFromStorage();
      loadFilterPreferences();

      setIsInitialized(true);
    };

    initializeComponent();
  }, [isStorageReady, isInitialized, loadFilterPreferences]);

  /**
   * Load posts whenever filter or selected profile changes
   */
  useEffect(() => {
    if (!isInitialized) return;
    loadPosts(1, false, false);
  }, [filterType, selectedProfileId, isInitialized]);

  /**
   * Socket listener for real-time refresh notifications
   */
  const { socket } = useSocket();
  useEffect(() => {
    socket?.on("reflections_refresh", (reflectionsRefresh: boolean) => {
      // Only show refresh pill when viewing own feed, not another user's data
      if (!selectedProfileId || selectedProfileId === user?.profile_id) {
        setShowTapRefresh(true);
      }
    });
    return () => {
      socket?.off("reflections_refresh");
    };
  }, [socket, selectedProfileId, user?.profile_id]);

  // ============ POST LOADING FUNCTIONS ============

  /**
   * Generate storage key based on filterType and selectedProfileId
   */
  const getStorageKey = useCallback(() => {
    if (selectedProfileId) {
      return `reflectionsFeed_${filterType}_${selectedProfileId}`;
    }
    return `reflectionsFeed_${filterType}`;
  }, [filterType, selectedProfileId]);

  /**
   * Load posts with caching and pagination support
   */
  const loadPosts = async (page = 1, append = false, forceReload = false) => {
    try {
      // Use different loading states for initial load vs infinite scroll
      if (!append) {
        setIsLoading(true);
      } else if (forceReload) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }
      setError(null);

      // Generate storage key for this filter combination
      const storageKey = getStorageKey();

      // Only load from storage on initial load, not when appending
      if (storage && isStorageReady && !forceReload && !append) {
        const storedPosts = await getIonicStorage(storage, storageKey);
        if (storedPosts) {
          setAllPosts(storedPosts);
          setIsLoading(false);
        }
      }

      const response = await fetchFeed(
        page,
        5,
        filterType,
        user?.profile_id !== selectedProfileId
          ? selectedProfileId != null
            ? +selectedProfileId
            : undefined
          : undefined
      );
      const transformedPosts = response.data as any;
      if (forceReload) {
        setAllPosts(transformedPosts);
        if (storage && isStorageReady) {
          await ionicStorage(
            storage,
            storageKey,
            JSON.stringify(transformedPosts)
          );
        }
      } else if (append) {
        setAllPosts((prevPosts) => [...prevPosts, ...transformedPosts]);
        if (storage && isStorageReady) {
          await ionicStorage(
            storage,
            storageKey,
            JSON.stringify([...allPosts, ...transformedPosts])
          );
        }
      } else {
        setAllPosts(transformedPosts);
        if (storage && isStorageReady) {
          await ionicStorage(
            storage,
            storageKey,
            JSON.stringify(transformedPosts)
          );
        }
      }
      setCurrentPage(page);
      setHasMorePosts(response.pagination.hasMore);
    } catch (error: any) {
      console.error("Error loading posts:", error);
      setError(error.message || "Failed to load posts");
      setHasMorePosts(false); // Disable infinite scroll on error
    } finally {
      if (!append) {
        setIsLoading(false);
      } else {
        setIsLoadingMore(false);
      }
    }
  };

  /**
   * Silent load of new posts and add them to feed without disrupting user
   */
  const loadNewPostsSilently = async () => {
    try {
      const response = await fetchFeed(
        1,
        5,
        filterType,
        user?.profile_id !== selectedProfileId
          ? selectedProfileId != null
            ? +selectedProfileId
            : undefined
          : undefined
      );
      const newPosts = response.data as Post[];

      // Generate storage key for this filter combination
      const storageKey = getStorageKey();

      // Add new posts to the beginning of the feed
      setAllPosts((prevPosts) => {
        const uniquePosts = [
          ...newPosts.filter(
            (newPost) => !prevPosts.find((p) => p.id === newPost.id)
          ),
          ...prevPosts,
        ];

        // Update storage silently in the background
        if (storage && isStorageReady) {
          ionicStorage(storage, storageKey, JSON.stringify(uniquePosts)).catch(
            (err) => console.error("Error updating storage:", err)
          );
        }

        return uniquePosts;
      });

      setHasMorePosts(response.pagination.hasMore);
    } catch (error: any) {
      console.error("Error silently loading new posts:", error);
    }
  };

  const applyFilter = useCallback(
    (posts: Post[], filter: string | undefined) => {
      if (!user?.profile_id) {
        return filter === "both" ? posts : [];
      }

      if (filter === "both") {
        return posts;
      } else if (filter === "Me") {
        const myPosts = posts.filter((post) => {
          const postAuthorId = Number(post.authorId);
          const currentUserId = Number(user.profile_id);
          return postAuthorId === currentUserId;
        });
        return myPosts;
      } else if (filter === "followers") {
        const followerPosts = posts.filter((post) => {
          const postAuthorId = Number(post.authorId);
          const currentUserId = Number(user.profile_id);
          return postAuthorId !== currentUserId;
        });
        return followerPosts;
      }
      return posts;
    },
    [user?.profile_id]
  );

  useEffect(() => {
    const filteredPosts = applyFilter(allPosts, filterType);
    setPosts(filteredPosts);
  }, [allPosts, filterType, applyFilter]);

  const loadMorePosts = async (event?: CustomEvent<void>) => {
    if (!hasMorePosts || isLoadingMore) {
      (event?.target as HTMLIonInfiniteScrollElement)?.complete();
      return;
    }

    try {
      // Add small delay to ensure loading spinner shows
      await new Promise((resolve) => setTimeout(resolve, 100));
      await loadPosts(currentPage + 1, true);
    } finally {
      // Complete the infinite scroll event once
      (event?.target as HTMLIonInfiniteScrollElement)?.complete();
    }
  };

  const handleRefresh = async (event: CustomEvent) => {
    try {
      await loadPosts(1, false, true);
      setSuccess("Posts refreshed!");
    } catch (error) {
      console.error("Error refreshing posts:", error);
    } finally {
      event.detail.complete();
    }
  };

  const handleFilterChange = useCallback(
    (newFilterType: typeof filterType) => {
      setFilterType(newFilterType);
      saveFilterPreferences(newFilterType, selectedProfileId, selectedProfile);
      setCurrentPage(1);
    },
    [selectedProfileId, selectedProfile, saveFilterPreferences]
  );

  const openAddPostModal = useCallback(() => {
    setIsAddPostModalOpen(true);
  }, []);

  const closeAddPostModal = async (refresh?: boolean) => {
    setIsAddPostModalOpen(false);
    if (refresh) {
      // Silently load new posts instead of forcing a full page reload
      await loadNewPostsSilently();
    }
  };

  /**
   * Handle new post creation - silently add to feed
   */
  const handleAddPost = async (postData?: any) => {
    try {
      setIsCreatingPost(true);
      if (postData) {
        // Generate storage key for this filter combination
        const storageKey = getStorageKey();

        // Add the new post to the beginning of the feed silently
        setAllPosts((prevPosts) => {
          const updatedPosts = [postData, ...prevPosts];
          // Update storage in the background
          if (storage && isStorageReady) {
            ionicStorage(
              storage,
              storageKey,
              JSON.stringify(updatedPosts)
            ).catch((err) => console.error("Error updating storage:", err));
          }
          // }
          return updatedPosts;
        });
        closeAddPostModal();

        if (storage && isStorageReady) {
          getIonicStorage(storage, `reflectionsFeed_Me`)
            .then((existingMePosts) => {
              const mePosts = Array.isArray(existingMePosts)
                ? existingMePosts
                : [];
              const updatedMePosts = [postData, ...mePosts];
              ionicStorage(
                storage,
                `reflectionsFeed_Me`,
                JSON.stringify(updatedMePosts)
              ).catch((err) =>
                console.error("Error updating my reflections:", err)
              );
            })
            .catch((err) =>
              console.error("Error fetching my reflections:", err)
            );
        }
      }
      setSuccess("Post created successfully!");
    } catch (error: any) {
      console.error("Error after post creation:", error);
      setError("Failed to add post to feed");
    } finally {
      setIsCreatingPost(false);
    }
  };

  const handleEditPost = useCallback(
    (postId: string) => {
      const postToEdit = posts.find((p) => p.id === postId);
      if (postToEdit) {
        setEditingPost(postToEdit);
        setIsEditModalOpen(true);
      }
    },
    [posts]
  );

  const closeEditModal = useCallback(() => {
    setIsEditModalOpen(false);
    setEditingPost(null);
  }, []);

  const handleEditComplete = async () => {
    try {
      setIsLoading(true);
      await loadPosts(1, false, true);
      closeEditModal();
    } catch (error: any) {
      console.error("Error after post update:", error);
      setError("Failed to refresh posts");
    }
  };

  const handleReactPost = useCallback(
    async (postId: string, reaction: ReactionType) => {
      const originalPosts = [...posts];
      const originalAllPosts = [...allPosts];

      try {
        const backendReaction = REACTION_TO_BACKEND_MAP[reaction];
        const numericPostId = parseInt(postId);

        const updatePostReaction = (post: Post) => {
          if (post.id === postId) {
            const updatedReactions = { ...post.reactions };

            if (post.userReaction === reaction) {
              updatedReactions[reaction] = Math.max(
                0,
                (updatedReactions[reaction] || 0) - 1
              );
              return {
                ...post,
                reactions: updatedReactions,
                userReaction: null,
              };
            } else {
              if (post.userReaction) {
                updatedReactions[post.userReaction] = Math.max(
                  0,
                  (updatedReactions[post.userReaction] || 0) - 1
                );
              }
              updatedReactions[reaction] =
                (updatedReactions[reaction] || 0) + 1;
              return {
                ...post,
                reactions: updatedReactions,
                userReaction: reaction,
              };
            }
          }
          return post;
        };

        setPosts((prevPosts) => prevPosts.map(updatePostReaction));
        setAllPosts((prevPosts) => prevPosts.map(updatePostReaction));

        await addCommentReaction(numericPostId, backendReaction);
      } catch (error: any) {
        console.error("Error toggling reaction:", error);
        setError("Failed to update reaction");
        setPosts(originalPosts);
        setAllPosts(originalAllPosts);
      }
    },
    [posts, allPosts]
  );

  const handleReplyPost = useCallback(
    async (postId: string) => {
      const post = posts.find((p) => p.id === postId) || null;
      setActivePost(post);
      setIsReplyOpen(true);
      setReplyPage(1);
      setReplyList([]);

      if (post) {
        const result = await fetchReplies(postId, 1);
        setReplyList(result.comments);
      }
    },
    [posts]
  );

  const fetchReplies = async (postId: string, page: number, limit = 50) => {
    try {
      const numericId = parseInt(postId);
      const response = await fetchCommentReplies(numericId, page, limit);
      const { data, pagination } = response;
      setTotalComments(pagination?.total || 0);
      return { comments: data || [], pagination };
    } catch (error) {
      console.error("Error fetching comments:", error);
      return { comments: [], pagination: null };
    }
  };

  const handleUpdatePost = useCallback(
    (
      postId: string,
      type: "visibility" | "remove",
      newVisibility?: "followers" | "private"
    ) => {
      if (type === "remove") {
        setPosts((prevPosts) => prevPosts.filter((post) => post.id !== postId));
        setAllPosts((prevPosts) =>
          prevPosts.filter((post) => post.id !== postId)
        );
      } else if (type === "visibility" && newVisibility) {
        setPosts((prevPosts) =>
          prevPosts.map((post) =>
            post.id === postId ? { ...post, visibility: newVisibility } : post
          )
        );
        setAllPosts((prevPosts) =>
          prevPosts.map((post) =>
            post.id === postId ? { ...post, visibility: newVisibility } : post
          )
        );
      }
    },
    []
  );

  const loadNextReplies = async (
    page = 1
  ): Promise<{ comments: any[]; pagination: any }> => {
    if (!activePost) return { comments: [], pagination: null };
    return await fetchReplies(activePost.id, page);
  };

  const handleSendReply = async (content: string, parentCommentId?: number) => {
    if (!activePost) return;
    const commentData = parentCommentId
      ? { content, parent_comment_id: parentCommentId }
      : { content };

    const created = await addComment(activePost.id, commentData);

    // Update posts reply count
    setPosts((prev) =>
      prev.map((p) =>
        p.id === activePost.id ? { ...p, replies: (p.replies || 0) + 1 } : p
      )
    );

    // Update allPosts reply count
    setAllPosts((prev) =>
      prev.map((p) =>
        p.id === activePost.id ? { ...p, replies: (p.replies || 0) + 1 } : p
      )
    );

    // Update total comments count
    setTotalComments((prev) => prev + 1);

    // Update replyList to include the new comment
    if (parentCommentId) {
      setReplyList((prev) =>
        prev.map((comment) => {
          if (comment.comment_id === parentCommentId) {
            return {
              ...comment,
              replies: [...(comment.replies || [], created)],
            };
          }
          return comment;
        })
      );
    } else {
      setReplyList((prev) => [...prev, created]);
    }

    return created as any;
  };

  const handleReactToReply = async (replyId: number, emoji: string) => {
    try {
      const result = await toggleCommentReaction(replyId, emoji);
      return result;
    } catch (e) {
      console.error("Failed to react to reply", e);
      throw e;
    }
  };

  // PostCard callback handlers
  const handleUnfollow = useCallback(async (authorId: number) => {
    setPopupContent({
      header: "Unfollow User",
      message: `Are you sure you want to unfollow this user? You won't see their posts in your feed.`,
      buttons: [
        {
          text: "Unfollow",
          action: async () => {
            try {
              await unfollowUser({ following_profile_id: authorId });
              loadPosts(1, false, true);
              setPopupOpen(false);
            } catch (error) {
              console.error("Failed to unfollow user", error);
              setPopupOpen(false);
            }
          },
          isPrimary: true,
        },
        {
          text: "Cancel",
          action: () => setPopupOpen(false),
          isPrimary: false,
        },
      ],
    });
    setPopupOpen(true);
  }, []);

  const handleChangePrivacy = useCallback(
    async (postId: string, currentVisibility: string, postData: PostData) => {
      setPopupContent({
        header: "Change Privacy",
        message:
          currentVisibility === "private"
            ? "Do you want to make this post visible to followers?"
            : "Do you want to make this post private?",
        buttons: [
          {
            text: "Cancel",
            action: () => setPopupOpen(false),
            isPrimary: false,
          },
          {
            text: "Yes",
            action: async () => {
              try {
                const newVisibility =
                  currentVisibility === "private" ? "followers" : "private";
                await changePostPrivacy(postId, newVisibility, postData);
                setPopupOpen(false);
                handleUpdatePost(postId, "visibility", newVisibility);
              } catch (error) {
                console.error("Failed to change privacy", error);
                setPopupOpen(false);
              }
            },
            isPrimary: true,
          },
        ],
      });
      setPopupOpen(true);
    },
    [handleUpdatePost]
  );

  const handleDeletePost = useCallback(
    async (postId: string) => {
      setPopupContent({
        header: "Delete Post",
        message:
          "Are you sure you want to delete this post? This action cannot be undone.",
        buttons: [
          {
            text: "Cancel",
            action: () => setPopupOpen(false),
            isPrimary: false,
          },
          {
            text: "Delete",
            action: async () => {
              try {
                await deletePost(postId);
                setPopupOpen(false);
                handleUpdatePost(postId, "remove");
                toaster.success("Deleted Successfully!", 2000, "bottom");
              } catch (error) {
                console.error("Failed to delete post", error);
                setPopupOpen(false);
              }
            },
            isPrimary: true,
          },
        ],
      });
      setPopupOpen(true);
    },
    [handleUpdatePost]
  );

  const handleShowAbout = useCallback(
    (authorId: number) => {
      const follower = following.filter((f) => f.id === authorId)[0];
      if (follower?.name || follower?.email || follower?.phone) {
        setPopupContent({
          header: "About this person",
          message: "",
          buttons: [
            {
              text: "Close",
              action: () => setPopupOpen(false),
              isPrimary: true,
            },
          ],
          children: (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                width: "100%",
              }}
            >
              {follower?.name && (
                <div>
                  <strong>Name:</strong> {follower.name}
                </div>
              )}
              {follower?.email && (
                <div>
                  <strong>Email:</strong> {follower.email}
                </div>
              )}
              {follower?.phone && (
                <div>
                  <strong>Phone:</strong> {follower.phone}
                </div>
              )}
            </div>
          ),
        });
      }
      setPopupOpen(true);
    },
    [following]
  );

  const showPopUpModal = () => {
    return (
      <>
        {popupOpen && (
          <ActionButtons
            isOpen={popupOpen}
            onClose={() => setPopupOpen(false)}
            title={popupContent.header}
            message={popupContent.message}
            actionButtons={popupContent.buttons}
          >
            {popupContent.children}
          </ActionButtons>
        )}
      </>
    );
  };

  // Read the BottomNav's published top-edge (set by BottomNav itself via
  // ResizeObserver). Fallback matches the CSS formula the nav uses so the
  // FAB doesn't jump if it mounts before the variable is published.
  const fabBottom =
    "var(--bottom-nav-clear, calc(max(env(safe-area-inset-bottom), 22px) + 82px))";

  const renderFollowing = () => {
    return (
      <>
        {isFollowingModalOpen && (
          <AllFollowingList
            following={following}
            isOpen={isFollowingModalOpen}
            onClose={closeFollowingModal}
            onSelect={(option) => {
              if (option && option.value) {
                const selectedId =
                  typeof option.value === "string"
                    ? parseInt(option.value)
                    : option.value;
                if (selectedId === user?.profile_id) {
                  // If user selects themselves, reset to "both" filter
                  setSelectedProfileId(null);
                  setSelectedProfile(null);
                  setFilterType("Me");
                  saveFilterPreferences("Me", null, null);
                  closeFollowingModal();
                  return;
                }
                setSelectedProfileId(selectedId);
                setSelectedProfile(option);
                setFilterType("one_follower");
                // Save the new filter preferences
                saveFilterPreferences("one_follower", selectedId, option);
              } else {
                setSelectedProfileId(null);
                setSelectedProfile(null);
                setFilterType("both");
                saveFilterPreferences("both", null, null);
              }
              closeFollowingModal();
            }}
            showMeOption={true}
          />
        )}
      </>
    );
  };

  return (
    <>
      <IonPage className="flex flex-col h-full journal-page">
        {isCreatingPost && <Loader loadingMessage={"Creating post"} />}

        <IonToast
          isOpen={!!error}
          message={error || ""}
          duration={3000}
          color="danger"
          onDidDismiss={() => setError(null)}
        />

        <IonToast
          isOpen={!!success}
          message={success || ""}
          duration={2000}
          color="success"
          onDidDismiss={() => setSuccess(null)}
        />

        <IonContent
          scrollY={
            isReplyOpen || isAddPostModalOpen || isFollowingModalOpen
              ? false
              : true
          }
          className="ion-no-padding"
        >
          <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
            <IonRefresherContent
              pullingText="Pull to refresh"
              refreshingSpinner="circles"
              refreshingText="Refreshing..."
            ></IonRefresherContent>
          </IonRefresher>

          <div className="journal-scrollable-content">
            <div className="journal-header-wrapper">
              <ReflectionHeader
                onOpenFollowingModal={() => {
                  setOpenFollowingModal(true);
                }}
                avatars={avatars as any}
                followingData={followingData}
                title={
                  !selectedProfileId && !selectedProfile
                    ? "Reflections"
                    : "Viewing "
                }
                subTitle={
                  user?.profile_id !== selectedProfileId &&
                    selectedProfileId &&
                    selectedProfile?.label
                    ? `${selectedProfile?.label}`
                    : ""
                }
              />
            </div>

            <div className="ion-padding journal-content">
              <ReflectionsModalCard
                onOpenModal={openAddPostModal}
                setPreSelected={setPreSelected}
              />
              {!selectedProfileId && (
                <div className="filter-buttons-container my-2  flex flex-row items-center justify-center gap-2 h-12 ">
                  {[
                    { label: "All", key: "both" },
                    { label: "My Reflections", key: "Me" },
                    { label: "Following", key: "followers" },
                  ].map(({ label, key }) => {
                    const isActive =
                      (filterType === key && !selectedProfileId) ||
                      (key === "both" &&
                        filterType === "both" &&
                        !selectedProfileId);

                    return (
                      <IonButton
                        key={key}
                        shape="round"
                        fill="clear"
                        className={`no-transform transition-all duration-200 font-medium rounded-full text-sm lg:text-lg md:text-md ${isActive ? "active-filter-btn" : "inactive-filter-btn"
                          }`}
                        onClick={() => {
                          handleFilterChange(key as any);
                          setSelectedProfileId(null);
                          setSelectedProfile(null);
                        }}
                      >
                        {label}
                      </IonButton>
                    );
                  })}
                </div>
              )}
              {/* Show selected Profile Name here with the cross on the top and hide the  */}
              {selectedProfileId && selectedProfile && (
                <div className="selected-profile-pill flex items-center justify-center py-2 mb-2">
                  <IonButton
                    shape="round"
                    fill="outline"
                    color="primary"
                    className="transition-all duration-200 font-medium rounded-full text-sm no-transform"
                    style={
                      {
                        "--text-transform": "none !important",
                      } as any
                    }
                    onClick={() => {
                      setSelectedProfileId(null);
                      setSelectedProfile(null);
                      handleFilterChange("both");
                    }}
                  >
                    {selectedProfile.label}
                    <span
                      style={{
                        marginLeft: "8px",
                        fontSize: "16px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                      }}
                      aria-label="Clear selected profile"
                    >
                      &#10005;
                    </span>
                  </IonButton>
                </div>
              )}

              {/* Show tap to refresh on click navigate to top and refresh the page  */}
              {showTapRefresh && (
                <DataRefreshPill
                  text="New Posts"
                  onClick={async () => {
                    setShowTapRefresh(false);
                    await loadNewPostsSilently();
                  }}
                  visible={showTapRefresh}
                />
              )}

              <div className="gap-2">
                {isLoading ? (
                  <CardSkeleton count={3} />
                ) : allPosts?.length === 0 && !isLoading ? (
                  <EmptyState
                    filterType={filterType}
                    selectedProfileId={selectedProfileId}
                    userProfileId={
                      user?.profile_id ? +user.profile_id : undefined
                    }
                    onCreatePost={openAddPostModal}
                  />
                ) : posts?.length === 0 ? (
                  // No posts matching current filter, but posts exist in server
                  <div style={{ textAlign: "center", padding: "40px 20px" }}>
                    <p style={{ color: "#999", fontSize: "14px" }}>
                      No posts match your current filter
                    </p>
                  </div>
                ) : (
                  posts?.map((post) => {
                    const isOwnPost =
                      user?.profile_id === Number(post.authorId);
                    const author = followingById.get(Number(post.authorId));

                    return (
                      <PostCard
                        key={post.id}
                        post={post}
                        userAvatar={
                          isOwnPost ? user?.avatar || "" : author?.avatar || ""
                        }
                        // Posts carry only userName, empty when the profile
                        // has no name; pass contacts to fall back on.
                        authorEmail={isOwnPost ? user?.email : author?.email}
                        authorPhone={isOwnPost ? user?.phone : author?.phone}
                        onReact={handleReactPost}
                        onReply={handleReplyPost}
                        currentUserId={
                          user?.profile_id ? +user.profile_id : undefined
                        }
                        onUnfollow={handleUnfollow}
                        onChangePrivacy={handleChangePrivacy}
                        onDelete={handleDeletePost}
                        onEdit={handleEditPost}
                        onShowAbout={handleShowAbout}
                      />
                    );
                  })
                )}
              </div>

              <IonInfiniteScroll
                onIonInfinite={loadMorePosts}
                threshold="400px"
              >
                <IonInfiniteScrollContent
                  loadingSpinner="bubbles"
                  loadingText="Loading more posts..."
                ></IonInfiniteScrollContent>
              </IonInfiniteScroll>
              <div style={{ height: "100px" }}></div>
            </div>
          </div>
        </IonContent>
        {isFollowingModalOpen && renderFollowing()}
        {isAddPostModalOpen && (
          <AddPostModal
            isOpen={isAddPostModalOpen}
            onDidDismiss={(refresh) => {
              closeAddPostModal(refresh);
            }}
            onPost={handleAddPost}
            preSelected={preSelected}
            user={user}
            setPreSelected={setPreSelected}
          />
        )}
        {editingPost && (
          <AddPostModal
            isOpen={isEditModalOpen}
            onDidDismiss={closeEditModal}
            onPost={handleAddPost}
            preSelected={null}
            user={user}
            setPreSelected={setPreSelected}
            editPost={editingPost}
            onEditComplete={handleEditComplete}
          />
        )}
        {activePost && (
          <CommentReplies
            isOpen={isReplyOpen}
            post={activePost}
            autoFocus={true}
            onClose={() => setIsReplyOpen(false)}
            onSendReply={handleSendReply}
            onReact={handleReactToReply}
            loadMoreComments={loadNextReplies as any}
            initialReplies={replyList}
            totalComments={totalComments}
          />
        )}
        {popupOpen && showPopUpModal()}
        <div
          style={{
            position: "fixed",
            bottom: fabBottom,
            right: "20px",
            zIndex: 1000,
          }}
        >
          <IonFabButton onClick={openAddPostModal} className="custom-fab">
            <IonIcon icon={add} className="text-white" />
          </IonFabButton>
        </div>
      </IonPage>
    </>
  );
};

export default MyReflections;
